/**
 * Error Boundary for Production Issues Feature
 *
 * Catches React errors within the Issues page and displays
 * a user-friendly fallback UI with recovery options.
 */
import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class IssuesErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to error reporting service (Sentry)
    console.error('IssuesErrorBoundary caught error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

function ErrorFallback({
  error,
  onRetry,
  onReload,
}: {
  error: Error | null;
  onRetry: () => void;
  onReload: () => void;
}) {
  const { t } = useTranslation('production');
  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl">
      <Card className="border-destructive">
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <div className="rounded-full bg-destructive/10 p-4 mb-4">
            <AlertTriangle
              className="h-12 w-12 text-destructive"
              aria-hidden="true"
            />
          </div>
          <h2 className="text-xl font-semibold mb-2">
            {t('issues.errorBoundary.title')}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            {t('issues.errorBoundary.description')}
          </p>
          {process.env.NODE_ENV === 'development' && error && (
            <pre className="mb-6 p-4 bg-muted rounded-lg text-left text-xs overflow-auto max-w-full max-h-32">
              {error.message}
            </pre>
          )}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onRetry}
              className="h-12 gap-2"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {t('issues.errorBoundary.retry')}
            </Button>
            <Button
              variant="default"
              onClick={onReload}
              className="h-12 gap-2"
            >
              {t('issues.errorBoundary.reloadPage')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
